import React, { useState, Component, lazy, Suspense, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Meta from '../components/Meta';
import apiClient from '../utils/axiosConfig';

// Lazy load the 3D component to avoid runtime errors
const Temple3DModel = lazy(() => 
  import('../components/Temple3DModel')
    .catch(() => ({ default: () => (
      <div className="h-full flex flex-col items-center justify-center bg-gray-100">
        <p className="text-gray-700">3D model failed to load. Displaying fallback view.</p>
        <div className="mt-4 w-32 h-32 bg-yellow-500 rounded-lg flex items-center justify-center">
          <span className="text-white font-bold">3D MODEL</span>
        </div>
      </div>
    )}))
);

// Error boundary to catch Three.js errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Three.js Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex flex-col items-center justify-center bg-gray-100">
          <p className="text-gray-700">3D model failed to load. Displaying fallback view.</p>
          <div className="mt-4 w-32 h-32 bg-yellow-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">3D MODEL</span>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function VirtualTemples({ defaultTempleId }) {
  const [selectedTemple, setSelectedTemple] = useState(null);
  const [showFlowers, setShowFlowers] = useState(false);
  const [showArti, setShowArti] = useState(false);
  const [isPlayingChanting, setIsPlayingChanting] = useState(false);
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  
  // Fetch temples from backend
  useEffect(() => {
    const fetchTemples = async () => {
      try {
        const response = await apiClient.get('/temple?limit=100');
        console.log('Received temples from API:', response.data);
        console.log('Number of temples:', response.data.length);
        // Check if any temple has modelFile or chantingAudio
        if (response.data && Array.isArray(response.data)) {
          response.data.forEach((temple, index) => {
            console.log(`Temple ${index}:`, {
              name: temple.name,
              hasModelFile: !!temple.modelFile,
              modelFile: temple.modelFile,
              hasChantingAudio: !!temple.chantingAudio,
              chantingAudio: temple.chantingAudio
            });
          });
        }
        setTemples(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching temples from API:', error);
        // Fallback to sample data if API fails
        setTemples([
          {
            _id: 1,
            name: "Ram Mandir",
            location: "Ayodhya, Uttar Pradesh",
            deity: "Lord Rama (Vishnu)",
            description: "The Ram Mandir is a Hindu temple dedicated to Lord Rama, located in Ayodhya, Uttar Pradesh, India. It is one of the most significant religious and cultural landmarks in India, representing the birthplace of Lord Rama as per Hindu scriptures. The temple was consecrated in January 2024 after years of construction based on traditional Vastu Shastra principles.",
            color: "#D4AF37",
            era: "Modern Period",
            architecturalStyle: "Nagar style with traditional Indian architecture",
            significance: "Symbol of cultural and religious heritage, built at the birthplace of Lord Rama according to the Ramayana",
            festivals: "Ram Navami (celebrated as the birthday of Lord Rama), Diwali (commemorating Rama's return to Ayodhya), Makar Sankranti",
            modelFile: { url: "ayodhya_ram_mandir.glb" }
          },
          {
            _id: 2,
            name: "Tirumala Venkateswara Temple",
            location: "Tirupati, Andhra Pradesh",
            deity: "Lord Venkateswara (Vishnu)",
            description: "One of the most visited religious places in the world, dedicated to Lord Venkateswara, a form of Vishnu. The temple is believed to have been built in the 9th century and is renowned for its architectural grandeur and spiritual significance.",
            color: "#8B4513",
            era: "Medieval Period",
            architecturalStyle: "Dravidian architecture",
            significance: "Considered the richest temple in the world, with vast treasures and donations",
            festivals: "Brahmotsavam, Vaikunta Ekadasi"
          },
          {
            _id: 3,
            name: "Vaishno Devi Temple",
            location: "Jammu and Kashmir",
            deity: "Goddess Vaishno Devi (Shakti)",
            description: "A Hindu temple dedicated to the Mother Goddess, located in a holy cave at an altitude of 5,200 feet. The cave has three forms of the Goddess - Mahakali, Mahalakshmi, and Mahasaraswati.",
            color: "#FFD700",
            era: "Ancient Period",
            architecturalStyle: "Natural cave temple",
            significance: "One of the 51 Shakti Peethas and a major Hindu pilgrimage site",
            festivals: "Navratri, Diwali, New Year"
          },
          {
            _id: 4,
            name: "Kedarnath Temple",
            location: "Uttarakhand",
            deity: "Lord Shiva",
            description: "One of the twelve Jyotirlingas, the holiest Hindu shrines of Shiva. Located in the Himalayas at an altitude of 3,583 meters, it is one of the most difficult temples to reach.",
            color: "#C0C0C0",
            era: "Ancient Period",
            architecturalStyle: "North Indian Nagara style",
            significance: "Part of the Char Dham pilgrimage and one of the Panch Kedars",
            festivals: "Shivratri, Shravan month",
            modelFile: { url: "kedarnath_temple.glb" }
          },
          {
            _id: 5,
            name: "Somnath Temple",
            location: "Gujarat",
            deity: "Lord Shiva",
            description: "One of the twelve Jyotirlinga shrines of Shiva, considered the first among them. The temple has been destroyed and rebuilt multiple times throughout history.",
            color: "#FFA500",
            era: "Ancient Period",
            architecturalStyle: "Chalukya style with mixed elements",
            significance: "First among the twelve Jyotirlingas and a symbol of resilience",
            festivals: "Shivratri, Kartik Purnima"
          },
          {
            _id: 6,
            name: "Ramanathaswamy Temple",
            location: "Tamil Nadu",
            deity: "Lord Shiva",
            description: "One of the twelve Jyotirlinga temples, located on Rameswaram island. The temple is associated with Lord Rama's pilgrimage to Lanka as mentioned in the Ramayana.",
            color: "#FF6347",
            era: "Medieval Period",
            architecturalStyle: "Dravidian architecture with multiple towers",
            significance: "One of the Char Dham pilgrimage sites and connected to Ramayana",
            festivals: "Maha Shivratri, Arudra Darshan"
          },
          {
            _id: 7,
            name: "Badrinath Temple",
            location: "Uttarakhand",
            deity: "Lord Vishnu",
            description: "One of the Char Dham pilgrimage sites, dedicated to Lord Vishnu. Located in the Garhwal hills at an altitude of 3,133 meters, it is surrounded by snowy peaks.",
            color: "#4169E1",
            era: "Ancient Period",
            architecturalStyle: "North Indian Nagara style",
            significance: "Part of the Char Dham and one of the 108 Divya Desams",
            festivals: "Badrinath Summer Festival, Shivratri"
          }
        ]);
        setLoading(false);
      }
    };

    fetchTemples();
  }, []);

  // Set default temple if specified
  useEffect(() => {
    if (defaultTempleId && temples.length > 0) {
      const temple = temples.find(t => t._id === defaultTempleId);
      if (temple) {
        setSelectedTemple(temple);
      }
    }
  }, [defaultTempleId, temples]);

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "VEDA Virtual Temples - 3D Temple Experience",
    "description": "Explore famous Indian temples in 3D virtual reality. Experience the architectural beauty and spiritual significance of ancient temples.",
    "url": "https://vedamerchandise.com/virtual-temples",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  // Function to handle temple selection
  const handleTempleSelect = (temple) => {
    setSelectedTemple(temple);
    // For Ram Mandir, navigate to SEO-friendly URL
    if (temple._id === 1) {
      navigate('/ram-mandir');
    }
  };

  // Function to handle flower offering
  const handleOfferFlowers = () => {
    // Trigger flower effect in iframe
    if (window.triggerFlowerEffect) {
      window.triggerFlowerEffect();
    }
    
    // Show flower overlay as fallback
    setShowFlowers(true);
    // Hide flowers after 3 seconds
    setTimeout(() => {
      setShowFlowers(false);
    }, 3000);
  };

  // Function to perform arti
  const handlePerformArti = () => {
    // Trigger arti effect in iframe
    if (window.triggerArtiEffect) {
      window.triggerArtiEffect();
    }
    
    // Show arti overlay as fallback
    setShowArti(true);
    // Hide arti after 3 seconds
    setTimeout(() => {
      setShowArti(false);
    }, 3000);
  };

  // Function to toggle chanting audio
  const toggleChanting = () => {
    if (isPlayingChanting) {
      // Stop audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlayingChanting(false);
    } else {
      // Play audio
      if (audioRef.current) {
        audioRef.current.play().catch(error => {
          console.log("Audio play failed:", error);
        });
      }
      setIsPlayingChanting(true);
    }
  };

  // Update audio source when temple changes
  useEffect(() => {
    if (selectedTemple) {
      // Reset audio state when changing temples
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlayingChanting(false);
      }
    }
  }, [selectedTemple]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-700">Loading virtual temples...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Meta 
        title="VEDA Virtual Temples - 3D Temple Experience"
        description="Explore famous Indian temples in 3D virtual reality. Experience the architectural beauty and spiritual significance of ancient temples."
        keywords="virtual temples, 3D temples, Indian temples, temple exploration, VR temples, VEDA temples"
        author="VEDA Merchandise"
        ogTitle="VEDA Virtual Temples - 3D Temple Experience"
        ogDescription="Explore famous Indian temples in 3D virtual reality. Experience the architectural beauty and spiritual significance of ancient temples."
        ogImage="https://vedamerchandise.com/temples-preview.jpg"
        ogUrl="https://vedamerchandise.com/virtual-temples"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="VEDA Virtual Temples - 3D Temple Experience"
        twitterDescription="Explore famous Indian temples in 3D virtual reality. Experience the architectural beauty and spiritual significance of ancient temples."
        twitterImage="https://vedamerchandise.com/temples-preview.jpg"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/virtual-temples"
        schemaMarkup={schemaMarkup}
      />
      
      {/* Add audio element for chanting - dynamically set source based on selected temple */}
      <audio ref={audioRef} preload="none">
        {selectedTemple && selectedTemple.chantingAudio && (
          <source 
            src={selectedTemple.chantingAudio.url} 
            type="audio/mpeg" 
          />
        )}
        Your browser does not support the audio element.
      </audio>
      
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-4 fredoka">
              <span className="text-red-500">Virtual</span>{' '}
              <span className="text-yellow-500">Temples</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-8">
              Explore the Architectural Marvels of Ancient India in 3D
            </p>
          </div>

          {/* Temple Selector */}
          {!selectedTemple && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {temples.map((temple) => (
                <div 
                  key={temple._id}
                  className={`bg-white rounded-2xl shadow-lg p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                    selectedTemple?._id === temple._id ? 'ring-4 ring-red-500' : ''
                  }`}
                  onClick={() => handleTempleSelect(temple)}
                >
                  <h3 className="text-2xl font-bold text-red-600 mb-2 fredoka">{temple.name}</h3>
                  <p className="text-gray-600 mb-2">{temple.location}</p>
                  <p className="text-gray-700 mb-4">{temple.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                      {temple.era}
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {temple.deity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 3D Temple Viewer - Enhanced Layout */}
          {selectedTemple && (
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-12">
              <div className="flex flex-col gap-8">
                {/* Temple Info Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-3xl font-bold text-red-600 mb-4 fredoka">{selectedTemple.name}</h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Location</h3>
                        <p className="text-gray-600">{selectedTemple.location}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Deity</h3>
                        <p className="text-gray-600">{selectedTemple.deity}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Description</h3>
                        <p className="text-gray-600">{selectedTemple.description}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Historical Era</h3>
                        <p className="text-gray-600">{selectedTemple.era}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Architectural Style</h3>
                        <p className="text-gray-600">{selectedTemple.architecturalStyle}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Religious Significance</h3>
                        <p className="text-gray-600">{selectedTemple.significance}</p>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 fredoka">Major Festivals</h3>
                        <p className="text-gray-600">{selectedTemple.festivals}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex flex-wrap gap-3">
                      {/* Offer Flowers Button */}
                      <button 
                        onClick={handleOfferFlowers}
                        className="bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-full transition-colors flex items-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Offer Flowers
                      </button>
                      
                      {/* Perform Arti Button */}
                      <button 
                        onClick={handlePerformArti}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full transition-colors flex items-center gap-2"
                      >
                        Perform Arti
                      </button>
                      
                      {/* Listen to Chanting Button */}
                      <button 
                        onClick={toggleChanting}
                        className={`${isPlayingChanting ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white px-4 py-2 rounded-full transition-colors flex items-center gap-2`}
                      >
                        {isPlayingChanting ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Stop Chanting
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                            Listen to Chanting
                          </>
                        )}
                      </button>
                      
                      <button 
                        onClick={() => setSelectedTemple(null)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-full transition-colors"
                      >
                        Back to Temples
                      </button>
                    </div>
                  </div>
                  
                  {/* 3D Viewer - Takes prominent space */}
                  <div className="h-96 lg:h-[500px] rounded-xl overflow-hidden relative">
                    <ErrorBoundary>
                      <Suspense fallback={
                        <div className="h-full flex flex-col items-center justify-center bg-gray-100">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mb-4"></div>
                          <p className="text-gray-700">{selectedTemple.modelFile ? 'Loading detailed 3D model...' : 'Loading 3D temple model...'}</p>
                        </div>
                      }>
                        <Temple3DModel temple={selectedTemple} />
                      </Suspense>
                    </ErrorBoundary>
                    
                    {/* Flower Animation Overlay (fallback) */}
                    {showFlowers && (
                      <div className="absolute inset-0 z-10 pointer-events-none">
                        {[...Array(20)].map((_, i) => (
                          <div 
                            key={i}
                            className="absolute text-2xl animate-bounce"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                              animationDelay: `${i * 0.1}s`,
                              fontSize: `${Math.random() * 24 + 16}px`
                            }}
                          >
                            🌸
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Arti Animation Overlay (fallback) */}
                    {showArti && (
                      <div className="absolute inset-0 z-10 pointer-events-none">
                        {[...Array(10)].map((_, i) => (
                          <div 
                            key={i}
                            className="absolute text-2xl animate-pulse"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                              animationDelay: `${i * 0.2}s`,
                              fontSize: `${Math.random() * 28 + 20}px`
                            }}
                          >
                            🪔
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg text-sm">
                      <p>Drag to rotate • Scroll to zoom</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Information Section */}
          {!selectedTemple && (
            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 text-white text-center shadow-xl mb-12">
              <h2 className="text-3xl font-bold mb-4 fredoka">Experience Sacred Architecture in Virtual Reality</h2>
              <p className="text-xl max-w-3xl mx-auto">
                Our virtual temple experience allows you to explore the intricate architecture and spiritual significance 
                of India's most revered temples from the comfort of your home. Each 3D model is carefully crafted 
                to represent the authentic structure and cultural importance of these sacred sites.
              </p>
              <div className="mt-6 bg-white bg-opacity-20 rounded-xl p-4 max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold mb-2 fredoka">How to Navigate</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                  <div className="flex items-start gap-2">
                    <span className="bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">1</span>
                    <p>Click on any temple to view its 3D model</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">2</span>
                    <p>Drag to rotate the temple and view from all angles</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">3</span>
                    <p>Use scroll wheel to zoom in and out for detailed views</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          {!selectedTemple && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
                <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Authentic 3D Models</h3>
                <p className="text-gray-700">
                  Each temple is meticulously recreated using architectural references and historical data 
                  to provide an authentic virtual experience.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
                <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Interactive Exploration</h3>
                <p className="text-gray-700">
                  Rotate, zoom, and pan to explore every angle of the temple architecture. 
                  Click and drag to view the structure from different perspectives.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
                <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Educational Value</h3>
                <p className="text-gray-700">
                  Learn about the history, significance, and architectural features of each temple 
                  through detailed descriptions and historical context.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}